import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_LANGUAGES, DEFAULT_LANGUAGE, normalizeLanguage } from '../i18n/languages';

/**
 * Métadonnées de la landing page selon la langue affichée.
 *
 * Le `<title>` et la description de `index.html` sont en français : ils
 * resteraient tels quels sur `/de` ou `/es`, jusque dans l'onglet du navigateur
 * et les partages de lien. Ce composant les réécrit et publie les `hreflang`
 * pour que chaque version soit indexée sur sa propre URL.
 *
 * Sans dépendance type react-helmet : quelques nœuds du `<head>` suffisent.
 */

const SITE_URL = 'https://www.focuspips.com';
const MANAGED_ATTR = 'data-landing-seo';

const upsertMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, key);
    node.setAttribute(MANAGED_ATTR, '');
    document.head.appendChild(node);
  }
  node.content = content;
};

export const LandingSeoHead = () => {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    const title = t('landing.seo.title');
    const description = t('landing.seo.description');

    document.title = title;
    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', language);

    // `index.html` déclare déjà un canonical : on le réécrit au lieu d'en
    // ajouter un second, deux canoniques concurrents annulant le signal.
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/${language}`;

    // Alternates : on remplace tout le jeu à chaque rendu plutôt que de patcher
    // les nœuds un par un — la liste est courte et toujours identique.
    document.head.querySelectorAll(`link[${MANAGED_ATTR}]`).forEach(node => node.remove());
    const addAlternate = (href: string, hreflang: string) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.href = href;
      link.hreflang = hreflang;
      link.setAttribute(MANAGED_ATTR, '');
      document.head.appendChild(link);
    };
    APP_LANGUAGES.forEach(l => addAlternate(`${SITE_URL}/${l.code}`, l.hreflang));
    addAlternate(`${SITE_URL}/${DEFAULT_LANGUAGE}`, 'x-default');
  }, [t, language]);

  return null;
};

export default LandingSeoHead;
